<template>

  <multiselect
    :selectLabel="$t('multiselect.select')"
    :selectGroupLabel="$t('multiselect.select')"
    tagPlaceholder=""
    :selectedLabel="$t('multiselect.selected')"
    :deselectLabel="$t('multiselect.deselect')"
    :hideSelected="hideSelected"

    :options="filteredOptions"
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :label="label"
    :placeholder="placeholder"
    :internalSearch="multiselectInternalSearch"

    :track-by="trackby"
    :multiple="multiple"
    :close-on-select="multiple ? false : true"
    :taggable="taggable"
    tag-position="top"

    :clear-on-select="false"
    :searchable="searchable"
    :loading="loading"
    :limit="multiselectLimit"
    :limit-text="multiselectLimitText"
    :disabled="disabled"
    @select="change"
    @search-change="onSearchChange"
    @tag="addTag"
    @open="onOpen"
    @close="onClose"
    @remove="onRemove"
  >
    <slot></slot>
    <template v-slot:noOptions>{{ $t('multiselect.noOptions') }}</template>
    <template v-slot:noResult>{{ $t('multiselect.noResult') }}</template>
    <template v-slot:maxElements>{{ $t('multiselect.maxElements') }}</template>
    <template #option="{ option, search, index }">
        <span :class="{ 'text-gray-400': option.$isDisabled }" :style="getIndentationStyle(option)">
          {{ option[label] }}
        </span>
    </template>
  </multiselect>
</template>

<script>

import 'vue-multiselect/dist/vue-multiselect.css';
import Multiselect from 'vue-multiselect'

export default {
  name: "MultselectForm",
  components: {Multiselect},
  emits: [
    "change",
    "search-change",
    "update:modelValue",
    "open",
    "close",
    "remove",
  ],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    options: {
      default: null,
    },
    modelValue: {
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    /** `true` + `searchable`: filtra `options` pelo texto (local). `false`: típico com `@search-change` / API. */
    internalSearch: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    limit: {
      default: null,
    },
    hideSelected: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    trackby: {
      type: String,
      default: '',
    },
    /**
     * Se true, exibe o rótulo ao atingir o limite de seleção (ex.: "1 item(s) selecionado(s)").
     * Por omissão fica oculto.
     */
    showLimitText: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // limit null → slice(0, null) = [] no vue-multiselect 3; undefined usa o default da lib
    multiselectLimit() {
      return this.limit == null ? undefined : this.limit
    },
    multiselectLimitText() {
      if (!this.showLimitText) {
        return () => ''
      }
      return (count) => this.$t('multiselect.itemsSelected', { count })
    },
    filteredOptions() {
      if (!this.shouldFilterLocally || !this.searchQuery) {
        return this.processedOptions
      }

      const normalizedSearch = this.normalizeSearchText(this.searchQuery)
      const options = this.processedOptions
      const matches = (option) => {
        const label = this.getOptionLabel(option)
        const searchText = option?.searchText || ''
        return this.normalizeSearchText(label).includes(normalizedSearch)
          || this.normalizeSearchText(searchText).includes(normalizedSearch)
      }

      // Hierarquia (ex.: Franqueado disabled + Unidades): mantém pai/filhos relacionados no filtro
      const matchedIndexes = new Set()
      options.forEach((option, index) => {
        if (!matches(option))
          return

        matchedIndexes.add(index)

        if (option.disabled && option.IdFranqueado != null) {
          for (let i = index + 1; i < options.length; i++) {
            const next = options[i]
            if (next.disabled)
              break
            if (next.IdFranqueado === option.IdFranqueado)
              matchedIndexes.add(i)
          }
        }

        if (!option.disabled && option.IdFranqueado != null) {
          for (let i = index - 1; i >= 0; i--) {
            const prev = options[i]
            if (prev.disabled && prev.IdFranqueado === option.IdFranqueado) {
              matchedIndexes.add(i)
              break
            }
          }
        }
      })

      return options.filter((_, index) => matchedIndexes.has(index))
    },
    multiselectInternalSearch() {
      return this.shouldFilterLocally ? false : this.internalSearch
    },
    processedOptions() {
      if (!this.options) return []

      return this.options.map(option => {
        // Se a opção tem a propriedade disabled, adiciona $isDisabled
        if (option.disabled) {
          return {
            ...option,
            $isDisabled: true
          }
        }
        return option
      })
    },
    shouldFilterLocally() {
      return this.searchable && this.internalSearch
    }
  },
  data() {
    return {
      searchQuery: ''
    }
  },
  methods: {
    addTag(newTag) {
    },
    change(item) {
      this.$emit('change', item)
    },
    getOptionLabel(option) {
      if (option == null) {
        return ''
      }

      if (this.label && typeof option === 'object') {
        return option[this.label] || ''
      }

      return option
    },
    getIndentationStyle(option) {

      if (!option?.level)
        return {};

      const level = this.calculateLevel(option.level);
      if (level === 0) {
        return {};
      }

      switch (level) {
        case 1: {
          return {
            paddingLeft: `${level * 8}px`
          };
        }
        case 2: {
          return {
            paddingLeft: `${level * 8}px`
          };
        }
        default:
          return null
      }


    },
    calculateLevel(level) {
      // leve ex: '1.1.1'
      // count de quantos pontos tem
      return level.split('.').length - 1;

    },
    normalizeSearchText(value) {
      return String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    },
    onSearchChange(searchQuery, id) {
      this.searchQuery = searchQuery || ''
      this.$emit('search-change', searchQuery, id)
    },
    onOpen() {
      this.$emit('open')
    },
    onClose() {
      this.$emit('close')
    },
    onRemove(item) {
      this.$emit('remove', item)
    },
  },
  watch: {
    options() {
      this.searchQuery = ''
    },
  },
}
</script>

<style scoped>
:deep(.multiselect) {
  min-width: 0;
  width: 100%;
}

:deep(.multiselect__tags) {
  overflow: hidden;
}

:deep(.multiselect__placeholder) {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.multiselect__input) {
  max-width: 100%;
}

:deep(.multiselect__input::placeholder) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-gray-400 {
  color: #9ca3af !important;
  cursor: not-allowed !important;
}

.text-gray-400:hover {
  background-color: transparent !important;
}

/* Estilos para melhorar a aparência da identação */
.multiselect__option {
  transition: padding-left 0.2s ease;

}

.multiselect__option:hover {
  background-color: #f8f9fa;
}

.multiselect__single {
  background: none;
  margin-bottom: 0;
}
</style>
